/**
 * usePresentationShare (web) — « Présenter » depuis un navigateur : partager le centre seul.
 *
 * Le port de `common/liveMode/liveV3/ScreenShareToggleButton.web.js`, à l'identique :
 *  - **avec `CropTarget`** (Chrome, Edge) : `CropTarget.fromElement(région)`, puis
 *    `getDisplayMedia({ video: { displaySurface: "browser" }, preferCurrentTab: true })`, la piste
 *    vidéo réduite à la région (`cropTo`), publiée en `screen_share` sous le nom `focus-screen-share` ;
 *    le bouton « Arrêter le partage » du navigateur finit la piste : elle est dépubliée ;
 *  - **sans `CropTarget`** (Safari, Firefox), ou sans région à recadrer : l'onglet entier, comme le
 *    bouton de la barre mais l'onglet courant proposé d'emblée (`preferCurrentTab`) ; `captureOf` le dit
 *    avant le geste, par la même règle (`cropRegion`), pour que l'écran réduise la page au centre avant
 *    que la piste ne parte ;
 *  - l'arrêt : la piste recadrée arrêtée puis dépubliée, sinon `setScreenShareEnabled(false)`.
 *
 * Deux écarts, sans effet sur la salle : la piste recadrée est tenue par une ref du crochet et non
 * par une variable du module (l'ancien bouton se démontait à chaque bascule, le crochet vit avec
 * l'écran) ; une capture prise mais jamais publiée (`cropTo` ou la publication en échec) est arrêtée
 * au lieu de continuer à filmer l'onglet.
 *
 * Le bouton de la barre (`webToggle`) est l'interrupteur de l'élève et de l'ancienne barre
 * (`useScreenShareToggle.web`), inchangé.
 */
import { LocalVideoTrack, Track, type Room } from "livekit-client"
import { useCallback, useEffect, useRef, useState } from "react"

import { type EngineTelemetry } from "../../shared/telemetry"
import {
    type PresentationCapture,
    isSharePickerDismissed,
    PRESENTATION_CROP_CAPTURE,
    PRESENTATION_FULL_TAB_OPTIONS,
    PRESENTATION_TRACK_NAME,
    type PresentationShare,
    useScreenSharePublished,
} from "./presentationShare"
import { useScreenShareToggle } from "./useScreenShareToggle.web"

/** La Region Capture du navigateur (Chrome) : absente des types du DOM. */
type CropTargetApi = { fromElement: (element: Element) => Promise<unknown> }
type CroppableTrack = MediaStreamTrack & { cropTo: (target: unknown) => Promise<void> }

/** Lu à chaque geste, et non au chargement comme l'ancien : un test le pose et le retire. */
function cropTargetApi(): CropTargetApi | null {
    const api = (globalThis as { CropTarget?: CropTargetApi }).CropTarget
    return api ?? null
}

/** Le nœud DOM de la région : sur le web, la ref d'une `View` est son élément. */
function regionElement(region: unknown): Element | null {
    if (typeof Element === "undefined" || !(region instanceof Element)) return null
    return region
}

/** De quoi recadrer `region` : la Region Capture du navigateur et l'élément ; `null` : l'onglet entier. */
function cropRegion(region: unknown): { api: CropTargetApi; element: Element } | null {
    const api = cropTargetApi()
    const element = api != null ? regionElement(region) : null
    return api != null && element != null ? { api, element } : null
}

/** Ce que `start(region)` partagera : la règle même de `start` (`cropRegion`). */
function captureOf(region: unknown): PresentationCapture {
    return cropRegion(region) != null ? "cropped" : "full"
}

export function usePresentationShare(room: Room | undefined, telemetry: EngineTelemetry): PresentationShare {
    const sharing = useScreenSharePublished(room)
    const webToggle = useScreenShareToggle(telemetry)
    const [pending, setPending] = useState(false)
    const [cropped, setCropped] = useState(false)
    const pendingRef = useRef(false)
    const cropTrackRef = useRef<LocalVideoTrack | null>(null)
    const roomRef = useRef(room)
    roomRef.current = room

    // Le partage retiré d'ailleurs (le bouton de la barre, une reprise) : plus de piste recadrée.
    useEffect(() => {
        if (sharing) return
        cropTrackRef.current = null
        setCropped(false)
    }, [sharing])

    const start = useCallback(
        async (region: unknown) => {
            const participant = roomRef.current?.localParticipant
            if (participant == null || pendingRef.current) return false
            pendingRef.current = true
            setPending(true)
            let captured: MediaStreamTrack | null = null
            try {
                const crop = cropRegion(region)
                if (crop != null) {
                    const target = await crop.api.fromElement(crop.element)
                    const stream = await navigator.mediaDevices.getDisplayMedia(
                        PRESENTATION_CROP_CAPTURE as unknown as DisplayMediaStreamOptions
                    )
                    const [videoTrack] = stream.getVideoTracks()
                    captured = videoTrack
                    await (videoTrack as CroppableTrack).cropTo(target)
                    const track = new LocalVideoTrack(videoTrack)
                    await participant.publishTrack(track, {
                        source: Track.Source.ScreenShare,
                        name: PRESENTATION_TRACK_NAME,
                    })
                    captured = null
                    cropTrackRef.current = track
                    setCropped(true)
                    // « Arrêter le partage » du navigateur : la piste recadrée est dépubliée.
                    videoTrack.onended = () => {
                        void (async () => {
                            try {
                                await participant.unpublishTrack(track)
                            } catch {
                                telemetry.transition("presenting", "off", "presentation_unpublish_failed")
                            }
                            if (cropTrackRef.current === track) {
                                cropTrackRef.current = null
                                setCropped(false)
                            }
                            telemetry.transition("presenting", "off", "presentation_ended_by_browser")
                        })()
                    }
                } else {
                    await participant.setScreenShareEnabled(true, PRESENTATION_FULL_TAB_OPTIONS)
                    setCropped(false)
                }
                telemetry.transition("off", "presenting", "presentation_started", { cropped: crop != null })
                return true
            } catch (cause) {
                captured?.stop()
                if (isSharePickerDismissed(cause)) telemetry.transition("off", "off", "presentation_dismissed")
                else telemetry.error("presentation_share", cause)
                return false
            } finally {
                pendingRef.current = false
                setPending(false)
            }
        },
        [telemetry]
    )

    const stop = useCallback(async () => {
        const participant = roomRef.current?.localParticipant
        if (participant == null || pendingRef.current) return false
        pendingRef.current = true
        setPending(true)
        try {
            const track = cropTrackRef.current
            if (track != null) {
                track.stop()
                await participant.unpublishTrack(track)
                cropTrackRef.current = null
            } else {
                await participant.setScreenShareEnabled(false)
            }
            setCropped(false)
            telemetry.transition("presenting", "off", "presentation_stopped")
            return true
        } catch (cause) {
            telemetry.error("presentation_stop", cause)
            return false
        } finally {
            pendingRef.current = false
            setPending(false)
        }
    }, [telemetry])

    return { sharing, cropped, pending, captureOf, start, stop, webToggle }
}
